<template>
    <div class="blog">
      <h1 class="center-header__title">{{ blog?.title }}</h1>
      <div class="container">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading Content...</p>
        </div>
  
        <div v-else-if="blog">
         
          <div class="text-end mb-5">
            <p class="blogPosts__tags">
              <span v-for="(tag, index) in blog.tags?.split(',')" :key="index" class="blue mx-2">
                {{ tag.trim() }}
              </span>
            </p>
          </div>
  
          
          <p>
            By <a :href="blog.authorLink" class="blue">{{ blog.author }}</a> on {{ blog.date }}
          </p>
  
          
          <div class="blog__introduction" v-html="blog.introduction" />
  
          
          <template v-if="blog.image1">
            <img
              v-if="isImageUrl(blog.image1)"
              :src="blog.image1"
              class="blog__img blog__img-big"
              :alt="blog.title"
            />
            <video
              v-else-if="isVideoUrl(blog.image1)"
              width="100%"
              height="400"
              controls
            >
              <source :src="blog.image1" type="video/mp4" />
            </video>
          </template>
  
         
          <div class="blog__description" v-html="blog.description" />
  
          
          <template v-if="blog.image2">
            <img
              v-if="isImageUrl(blog.image2)"
              :src="blog.image2"
              class="blog__img blog__img-second"
              :alt="blog.title"
            />
            <video
              v-else-if="isVideoUrl(blog.image2)"
              width="100%"
              height="400"
              controls
            >
              <source :src="blog.image2" type="video/mp4" />
            </video>
          </template>
  
          
          <div class="blog__conclusion" v-html="blog.conclusion" />
  
          
          <div class="blogPosts__box mt-5" v-if="secondNewestBlog">
            <div class="blogDemo__box mt-4">
              <router-link to="/blogFile" class="blue">Back to Blogs</router-link>
              <div class="blogDemo__content mt-4">
                <div class="blogDemo__imgbox">
                  <img
                    v-if="isImageUrl(secondNewestBlog.image1)"
                    :src="secondNewestBlog.image1"
                    class="blogDemo__img"
                    :alt="secondNewestBlog.title"
                  />
                  <video
                    v-else-if="isVideoUrl(secondNewestBlog.image1)"
                    width="100%"
                    height="400"
                    controls
                  >
                    <source :src="secondNewestBlog.image1" type="video/mp4" />
                  </video>
                </div>
                <div class="blogDemo__details">
                  <p class="blogDemo__tags">
                    <span
                      v-for="(tag, index) in secondNewestBlog.tags?.split(',')"
                      :key="index"
                      class="blue mx-2"
                    >
                      {{ tag.trim() }}
                    </span>
                  </p>
                  <h3 class="blogDemo__title">{{ secondNewestBlog.title }}</h3>
                  <router-link :to="`/blog/${secondNewestBlog.id}`" class="blue">
                    Read more
                  </router-link>
                </div>
              </div>
            </div>
          </div>
  
          
          <GetInTouch />
        </div>
  
        <div v-else>
          <p>Blog not found.</p>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { getDatabase, ref as dbRef, get } from 'firebase/database';
  import { app } from '@/firebase/firebase'; 
  import GetInTouch from '@/components/GetInTouch.vue';
  
  const route = useRoute();
  const blog = ref(null);
  const secondNewestBlog = ref(null);
  const loading = ref(true);
  const db = getDatabase(app);
  
  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(url);
  const isVideoUrl = (url) => /\.(mp4|mov|wmv|avi|mkv)$/i.test(url);
  
  const fetchBlog = async (id) => {
    loading.value = true;
    try {
      const snapshot = await get(dbRef(db, `blogs/${id}`));
      blog.value = snapshot.exists() ? snapshot.val() : null;
    } catch (e) {
      console.error('Error fetching blog:', e);
      blog.value = null;
    } finally {
      loading.value = false;
    }
  };
  
  const fetchSecondNewestBlog = async () => {
    try {
      const snapshot = await get(dbRef(db, 'blogs'));
      if (snapshot.exists()) {
        const blogs = snapshot.val();
        const list = Object.entries(blogs).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.date) - new Date(a.date));
        if (list.length > 1) secondNewestBlog.value = list[1];
      }
    } catch (e) {
      console.error('Error fetching second newest blog:', e);
    }
  };
  
  onMounted(() => {
    fetchBlog(route.params.id);
    fetchSecondNewestBlog();
    window.scrollTo(0, 0);
  });
  
  watch(() => route.params.id, (newId) => {
    fetchBlog(newId);
    window.scrollTo(0, 0);
  });
  </script>
  
  <style scoped>
  
  .blog .container {
  padding: 0 15px;
}

.center-header__title {
  font-size: 2.5rem;
  margin-top: 5px;
  margin-bottom: 20px;
  
}

.blog__text {
  text-align: justify;
  margin-bottom: 20px;
}

.blog__text p {
  font-size: 16px;
  color: #333;
  text-align: center;
}

.blog__text a {
  color: #007bff;
  text-decoration: none;
}

.blog__text a:hover {
  text-decoration: underline;
}

.blog__tags {
  font-size: 14px;
  color: #007bff;
  margin-bottom: 10px;
}

.blog__tags span {
  margin-right: 10px;
  font-weight: 500;
}

.blog__img,
.blog__img-big,
.blog__img-second {
  
  height: 90vh;
  object-fit: cover;
  margin-bottom: 20px;
}

.blog__video {
  width: 100%;
  margin-bottom: 20px;
}

.blog__video video {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: contain;
}

.blog__description,
.blog__conclusion,
.blog__introduction {
  font-size: 16px;
  color: #555;
  margin-top: 20px;
  text-align: left;
}

.blogPosts__box {
  margin-top: 40px;
  margin-bottom: 50px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.blogDemo__box {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.blogDemo__content {
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding: 20px;
}

.blogDemo__imgbox {
  flex: 1;
  height: 250px;
  overflow: hidden;
  position: relative;
}

.blogDemo__imgbox img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blogDemo__imgbox video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 250px;
}

.blogDemo__details {
  flex: 2;
  padding: 15px;
}

.blogDemo__tags {
  font-size: 14px;
  color: #007bff;
  margin-bottom: 10px;
}

.blogDemo__title {
  font-size: 1.6rem;
  font-weight: bold;
  margin-bottom: 10px;
  color: black;
}

.blogDemo__author {
  font-size: 14px;
  color: #555;
  margin-bottom: 10px;
}

.blogDemo__introduction {
  font-size: 14px;
  color: #333;
  margin-bottom: 20px;
}

.text-end a {
  font-size: 14px;
  color: #007bff;
  text-decoration: none;
}

.text-end a:hover {
  text-decoration: underline;
}

@media (max-width: 767px) {
  .center-header__title {
    font-size: 2rem;
  }

  .blog__text {
    font-size: 14px;
  }

  .blogDemo__content {
    flex-direction: column;
  }

  .blogDemo__imgbox {
    height: 200px;
  }

  .blogDemo__details {
    padding: 10px;
  }

  .blogDemo__title {
    font-size: 1.4rem;
  }

  .blogDemo__author {
    font-size: 13px;
  }

  .blogDemo__introduction {
    font-size: 13px;
  }

  .text-end a {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .center-header__title {
    font-size: 1.6rem;
  }

  .blog__text {
    font-size: 12px;
  }

  .blogDemo__imgbox {
    height: 180px;
  }

  .blogDemo__title {
    font-size: 1.2rem;
  }

  .blogDemo__author {
    font-size: 12px;
  }

  .blogDemo__introduction {
    font-size: 12px;
  }
}
  </style>
  